import {Component, Prop, Mixins, Watch} from 'vue-property-decorator';
import LoadingMixin from '@/mixins/LoadingMixin';
import {
    Service, ProductType, Product } from '@/products-and-services/ProductsAndServicesModels';
import IconsMixin from '@/mixins/IconsMixin';
import ProductsServicesMixin from '@/mixins/ProductsServicesMixin';
import ContentCard from '@/products-and-services/products-type/ContentCard.vue';
import Checkboxes from '@/products-and-services/products-type/Checkboxes.vue';
import PhoneOptions from '@/products-and-services/products-type/PhoneOptions.vue';
import PhoneNumberPublishing from '@/products-and-services/products-type/PhoneNumberPublishing.vue';
import BatteryBackup from '@/products-and-services/products-type/BatteryBackup.vue';
import AvastAnitiVirusEmail from '@/products-and-services/products-type/AvastAnitiVirusEmail.vue';
import CustomEmailAddresses from '@/products-and-services/products-type/CustomEmailAddresses.vue';
import AdditionalDvr from '@/products-and-services/products-type/AdditionalDvr.vue';
import NumberOfTv from '@/products-and-services/products-type/NumberOfTv.vue';
import CloudBackup from '@/products-and-services/products-type/CloudBackup.vue'
import ProductMixin from '@/mixins/ProductMixin';
import ThemeMixin from "@/mixins/ThemeMixin";
import StaticIPAddresses from "@/products-and-services/products-type/StaticIPAddresses.vue";
import SectionSummary from "@/views/products-and-services/components/SectionSummary.vue";
import { SectionItem } from "@/views/products-and-services/components/SectionInterfaces";
import DividedTitle from "@/views/products-and-services/components/DividedTitle.vue";
import BusinessTv from "@/products-and-services/products-type/BusinessTv.vue";
import FinishedButton from "@/views/products-and-services/components/FinishedButton.vue";
import StreamingDevices from "@/products-and-services/products-type/StreamingDevices.vue";


@Component({
    components: {
        BusinessTv,
        ContentCard,
        Checkboxes,
        DividedTitle,
        FinishedButton,
        PhoneOptions,
        PhoneNumberPublishing,
        BatteryBackup,
        NumberOfTv,
        AdditionalDvr,
        CustomEmailAddresses,
        AvastAnitiVirusEmail,
        CloudBackup,
        StaticIPAddresses,
        SectionSummary,
        StreamingDevices
    },
})
export default class ProductsView extends Mixins(LoadingMixin, IconsMixin, ProductsServicesMixin, ProductMixin, ThemeMixin) {
    @Prop({ type: String, required: false })
    private serviceTypeId!: string;
    @Prop({ type: String, required: false })
    private serviceId!: string;
    @Prop({ type: Boolean, required: false })
    private showCompleteView!: boolean;
    @Prop({ type: String, required: false })
    private currentSelectedServiceTypeId!: string;
    @Prop({ type: [Number], required: false, default: 2 })
    private step!: number;

    private isEditing: boolean = false;

    get productSummaryItems(): SectionItem[] {
        const products: Product[] = this.products || [];
        return products.map(product => ({id: product.productId, catId: product.catId, name: product.name, amount: product.totalAmount}));
    }

    @Watch("currentSelectedServiceTypeId")
    isEditable() {
        // isEditing is responsible for knowing whether or not the user took action on editing products and second check sees if they moved to a different service
        this.isEditing = this.isEditing && this.currentSelectedServiceTypeId === this.serviceTypeId;
    }

    @Watch("$store.state.validation")
    doErrorsExist(){
        this.scrollErrorIntoView();
    }

    get service(): Service {
        const service = this.$store.getters.getServiceByTypeIdAndId(this.serviceTypeId, this.serviceId);
        return this.getManagedService(service);
    }

    get serviceTypeName(): string {
        return this.$store.getters.getServiceTypeById(this.serviceTypeId).name;
    }

    get products(): Product[] | undefined {
        const service = this.$store.state.shoppingCartData.selectedOptions.services.find(
            (service: Service) => service.serviceId === this.service.serviceId
        );

        const selectedProducts = (service) ? service.products : undefined;

        if (selectedProducts && selectedProducts.length > 0) {
            return selectedProducts;
        }
    }

    private updated() {
            this.scrollErrorIntoView();
    }

    private scrollErrorIntoView() {
        let idToScrollTo = "";
        if (this.isMissingPhoneOption) {
            idToScrollTo = "phoneOption";
        } else if (this.isMissingBatteryBk) {
            idToScrollTo = "batteryBackup";
        } else if (this.isMissingDirectoryOption) {
            idToScrollTo = "phoneNumberPublishing";
        } else if (this.hasInvalidEmailAddresses) {
            idToScrollTo = "customEmail";
        } else if (this.hasInvalidCloudBackupEmail) {
            idToScrollTo = "cloudBackup";
        } else if (this.hasInvalidTopBoxLeaseOption) {
            idToScrollTo = "topBoxLease"
        } else if (this.hasInvalidAvastEmail) {
            idToScrollTo = "avast";
        } else if (this.hasInvalidVoicemailPlusEmail) {
            idToScrollTo = "voicemailPlus"
        } else if (this.hasInvalidStaticIpOption) {
            idToScrollTo = "static-ip-addresses"
        } else if(this.hasInvalidStreamingDevices) {
            idToScrollTo = "streaming-devices"
        }
        if (idToScrollTo && document.getElementById(idToScrollTo)) {
            // We need to wait for animations to finish in case one accordion is expanding and another is closing.
            // Otherwise the scroll messes up and tries to scroll to the previously opened accordion
            setTimeout(() => {
                document.getElementById(idToScrollTo)!.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 250);
        }
    }

    private mounted() {
        // We need to wait for animations to finish in case one accordion is expanding and another is closing.
        // Otherwise the scroll messes up and tries to scroll to the previously opened accordion
        setTimeout(() => {
            document.getElementById(`products-${this.serviceId}`) &&
            document.getElementById(`products-${this.serviceId}`)!.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 250);
    }

    isTelephoneFeatures(products: Product[]) {
        if (products.length) {
            const [prod] = products;
            return prod.typeId && prod.typeId.toString() === ProductType.CHECKBOX.toString();
        }
        return false;
    }

    private editClicked() {
        this.isEditing = true;
        this.$emit("edit-clicked", this.serviceTypeId);
    }

    private completeProductSelection() {
        if (this.$store.getters.getErrors() && this.$store.getters.getErrors().length > 0) {
            this.shouldTriggerValidation(true);
        }
        else {
            this.shouldTriggerValidation(false);
            let targetIndex = 0;
            let currentIndex = 0;

            const totalServiceTypes = document.getElementsByClassName("service-type-scroller").length;
            const currentServiceType = Array.from(document.getElementsByClassName("service-type-scroller"))
                .find(element => element.getAttribute("data-service-type-id") === this.serviceTypeId);

            if (currentServiceType && currentServiceType.getAttribute("id")) {
                const indexId = currentServiceType.getAttribute("id") || '0';
                currentIndex = parseInt(indexId.replace("service-type-index-", ""), 10);
            }

            if (totalServiceTypes === (currentIndex + 1)) {
                targetIndex = currentIndex;
            } else {
                targetIndex = currentIndex + 1;
            }

            setTimeout(() => {
                document.getElementsByClassName("service-type-scroller")[targetIndex].scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 200);

            this.isEditing = false;
            this.$emit("products-complete");
        }
    }
}
